import React, { useState, useEffect } from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { SAVE_ANSWER } from "../../../../services/constants";
import axios from "axios";
import {
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  LinearProgress,
  TextField,
  Box,
} from "@mui/material";
import styles from "./answer.module.scss";
import { useNavigate } from "react-router-dom";
import AddProjectStore from "../../../../store/AddProjectStore";

const ques_arr = [
  {
    key: "organisation",
    ques: "Which of the following best describes you or your organization?",
    options: [
      "An SEO agency",
      "A marketing agency",
      "A marketing department at a bigger company",
      "Myself (I own a niche blog)",
      "Myself (I own a small business)",
      "A large media company",
    ],
  },
  {
    key: "emp_size",
    ques: "How many people work at your company or organization?",
    options: ["It's just me", "2-5", "6-20", "21-50", "51-500", "500+"],
  },
  {
    key: "tools",
    ques: "Have you ever used any of the following keyword research tools?",
    options: ["Ahrefs", "Semrush", "Mangools", "SE Ranking", "SpyFu", "Other"],
  },
  {
    key: "experience",
    ques: "How would you describe your level of experience in SEO?",
    options: [
      "I'm just starting to learn SEO",
      "I have a basic understanding of SEO principles",
      "I have a moderate amount of experience with SEO",
      "I have extensive experience and knowledge in SEO",
      "I'm an SEO expert with advanced skills",
    ],
  },
  {
    key: "referral",
    ques: "Where did you hear about us?",
    options: [
      "Google Search",
      "Online advertising",
      "Website or blog",
      "Email marketing",
      "Forum or online community",
      "Existing User",
      "Linkedin",
      "Reddit",
      "Other Social Media",
      "Others",
    ],
  },
];

export default function AnswerDialog() {
  const navigate = useNavigate();
  const [selectedAns, setSelectedAns] = useState({});
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState("");
  const [otherText, setOtherText] = useState(""); // For handling "Others"

  const { user_answer } = AddProjectStore((state) => state);

  useEffect(() => {
    if (!user_answer.email) {
      navigate("/projects");
    }
  }, [user_answer.email]);

  const handleChange = (event) => {
    const { value } = event.target;
    setSelectedOption(value);

    const currentKey = ques_arr[currentQuestionIndex].key;

    // Save answer to state dynamically
    setSelectedAns((prevAns) => ({
      ...prevAns,
      [currentKey]: value === "Others" ? otherText : value,
    }));
  };

  const handleOtherTextChange = (event) => {
    setOtherText(event.target.value);
    const currentKey = ques_arr[currentQuestionIndex].key;

    setSelectedAns((prevAns) => ({
      ...prevAns,
      [currentKey]: event.target.value,
    }));
  };

  const handleSaveAnswer = () => {
    const config = {
      method: "post",
      maxBodyLength: Infinity,
      url: SAVE_ANSWER(),
      headers: {
        "Content-Type": "application/json",
      },
      data: {
        email: user_answer.email,
        data: JSON.stringify(selectedAns),
      },
    };

    axios
      .request(config)
      .then(() => {
        setTimeout(() => {
          navigate("/login");
        }, 1000);
      })
      .catch((error) => {
        console.error("Error saving answer:", error);
        navigate("/login");
        // Handle error state or display message
      })
      .finally(() => {
        AddProjectStore.setState({
          user_answer: {
            open: false,
            email: null,
          },
        });
      });
  };

  const handleContinue = () => {
    if (currentQuestionIndex < ques_arr.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setSelectedOption("");
    } else {
      handleSaveAnswer();
    }
  };

  const progressValue = ((currentQuestionIndex + 1) / ques_arr.length) * 100;

  const isOtherSelected = selectedOption === "Others";
  const isContinueDisabled = !selectedOption || (isOtherSelected && !otherText);

  return (
    <Dialog
      open={user_answer.open}
      onClose={() => {}}
      sx={{
        "& .MuiDialog-container": {
          backgroundColor: "#D8D9E9",
        },
        "& .MuiPaper-root": {
          padding: "15px",
          backgroundColor: "#E9E9E9",
          width: {
            xs: "90%", // Small devices (xs)
            sm: "60%", // Small devices
            md: "40%", // Medium devices
          },
          maxWidth: "600px", // Set a max width for large screens
          margin: "auto",
        },
      }}
      maxWidth="md" // This controls the Dialog's overall width at medium sizes
      fullWidth
    >
      <DialogTitle>{ques_arr[currentQuestionIndex].ques}</DialogTitle>
      <DialogContent>
        <FormControl component="fieldset" className={styles.ques_opt_cont}>
          <RadioGroup value={selectedOption} onChange={handleChange}>
            {ques_arr[currentQuestionIndex].options.map((option, index) => (
              <FormControlLabel
                key={index}
                value={option}
                control={<Radio />}
                label={option}
                sx={{
                  width: "100%",
                  border: "1px solid",
                  borderColor:
                    selectedOption === option
                      ? "#E95655"
                      : "rgba(0, 0, 0, 0.6)", // Red border when selected
                  borderRadius: "5px",
                  minHeight: "50px",
                  margin: "8px 0",
                  "& .MuiTypography-root": {
                    fontWeight: "500",
                  },
                }}
              />
            ))}
          </RadioGroup>

          {/* Conditionally render textbox for 'Others' option */}
          {selectedOption === "Others" && (
            <TextField
              fullWidth
              margin="normal"
              value={otherText}
              placeholder="Please specify"
              onChange={handleOtherTextChange}
              sx={{
                "& .MuiInputBase-root": {
                  maxHeight: "50px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(0, 0, 0, 0.6)",
                },
              }}
            />
          )}
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Box className="d-flex flex-column align-items-end w-100">
          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={{ marginBottom: "20px", width: "100%" }}
          />
          <Button
            className={styles.button}
            sx={{ maxWidth: "130px" }}
            onClick={handleContinue}
            variant="contained"
            disabled={isContinueDisabled} // Disable button if no option or 'Others' textbox is empty
          >
            Continue
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
